import { useCallback, useEffect } from 'react';
import { ClickAwayListener, IconButton, Paper, Popper, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ANNOTATION_REGISTRY,
  type CellAnnotationCapabilities,
  getApplicableAnnotationTypes,
} from './annotationRegistry';
import type { CellAnnotationType } from './types';

// Messenger-style annotation-type picker. Renders one icon per applicable
// annotation type; clicking an icon delegates to the host (FOD/CFL pages in
// Tasks #8/#9) which mounts the type-specific popout. The bar itself owns
// nothing about how each type renders — that's the registry's job.

const BarPopper = styled(Popper)(({ theme }) => ({
  // Bar sits above any open spreadsheet/dialog scrim; modal zIndex matches
  // the cross-reference popper so the two never fight over stacking.
  zIndex: theme.zIndex.modal,
}));

const BarPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  // Tighter spacing per design tweaks: small gap between icons, narrow paper
  // padding so the bar reads as a compact toolbar tucked above the cell
  // rather than a chunky floating pill.
  gap: 3,
  padding: '2px 3px',
  borderRadius: 4,
  boxShadow: theme.shadows[6],
}));

const BarIconButton = styled(IconButton)({
  // Slim button padding so each icon is easy to click without inflating the
  // bar's overall height; SVG sized to match the design tweak.
  padding: '2px 4px',
  '& svg': {
    width: 14,
    height: 14,
  },
});

export interface CellAnnotationBarProps {
  // Whether the bar is visible. Host owns open/close so the same anchor can
  // drive both hover and any external teardown.
  open: boolean;
  // Anchor element for the Popper — typically the cell viewer container.
  // Null when closed.
  anchorEl: HTMLElement | null;
  // Capabilities of the anchor cell. Drives which annotation types render.
  capabilities: CellAnnotationCapabilities;
  // Called on Escape, or after a type is picked.
  onClose: () => void;
  // Called when the user picks a type. Host opens the relevant popout. The
  // bar auto-closes after onSelectType fires (auto-close is the explicit
  // controller decision — user picked one, hide the bar).
  onSelectType: (type: CellAnnotationType) => void;
  // Hover bridge: the parent (CellAnnotationValueViewer) starts a close timer
  // when the cursor leaves the host cell. Re-entering the bar must cancel
  // that timer; leaving the bar must restart it. The parent owns the timer
  // because the cell→bar handoff straddles two siblings.
  onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
}

const CellAnnotationBar: React.FC<CellAnnotationBarProps> = ({
  open,
  anchorEl,
  capabilities,
  onClose,
  onSelectType,
  onMouseEnter,
  onMouseLeave,
}) => {
  const applicableTypes = getApplicableAnnotationTypes(capabilities);

  const handleSelect = useCallback(
    (type: CellAnnotationType) => {
      onSelectType(type);
      onClose();
    },
    [onSelectType, onClose]
  );

  // Escape handler is attached to the document while open so the bar closes
  // even if focus is on the anchor cell rather than inside the Popper.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // No applicable types — render nothing rather than an empty pill, since
  // the bar exists to offer choices.
  if (!open || applicableTypes.length === 0) {
    return null;
  }

  return (
    <BarPopper open={open} anchorEl={anchorEl} placement="top" data-testid="cell-annotation-bar">
      <ClickAwayListener onClickAway={onClose}>
        <BarPaper
          elevation={6}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}>
          {applicableTypes.map(type => {
            const { icon: Icon, label } = ANNOTATION_REGISTRY[type];
            return (
              <Tooltip key={type} title={label} placement="top">
                <BarIconButton
                  size="small"
                  aria-label={label}
                  data-testid={`cell-annotation-bar-option-${type}`}
                  onMouseDown={event => event.stopPropagation()}
                  onClick={event => {
                    event.stopPropagation();
                    handleSelect(type);
                  }}>
                  <Icon />
                </BarIconButton>
              </Tooltip>
            );
          })}
        </BarPaper>
      </ClickAwayListener>
    </BarPopper>
  );
};

export default CellAnnotationBar;
